import { HitData, PitchData } from "../types/gameFull";

/**
 * Metric tiles for the At-Bat Metrics view. Consolidates V1's
 * dataTitles / tileUnits / tile-options lookups into one list.
 */

export type MetricGroup = "pitch" | "hit";

type PitchMetric = {
  id: string;
  label: string;
  unit: string;
  group: "pitch";
  field: keyof PitchData;
};

type HitMetric = {
  id: string;
  label: string;
  unit: string;
  group: "hit";
  field: keyof HitData;
};

export type MetricDefinition = PitchMetric | HitMetric;

const pitch = (
  id: string,
  label: string,
  unit: string,
  field: keyof PitchData,
): PitchMetric => ({ id, label, unit, group: "pitch", field });

const hit = (
  id: string,
  label: string,
  unit: string,
  field: keyof HitData,
): HitMetric => ({ id, label, unit, group: "hit", field });

export const METRICS: MetricDefinition[] = [
  pitch("velocity", "Velocity", "MPH", "RelSpeedMPH"),
  pitch("spinRate", "Spin Rate", "RPM", "SpinRateRPM"),
  pitch("efficiency", "Efficiency", "Percent", "yt_SpinEfficiencyPercent"),
  pitch("tilt", "Tilt", "O' Clock", "Tilt"),
  pitch("spinAxis", "Spin Axis", "Degrees", "SpinAxisDegrees"),
  pitch("vertBreak", "Vert. Break", "Inches", "VertBreakInches"),
  pitch("inducedVertBreak", "I. Vert BR", "Inches", "InducedVertBreakInches"),
  pitch("horzBreak", "Horz. Break", "Inches", "HorzBreakInches"),
  pitch("vertApprAngle", "Vert. Apr Ang.", "Degrees", "VertApprAngleDegrees"),
  pitch("horzApprAngle", "Horz. Apr Ang.", "Degrees", "HorzApprAngleDegrees"),
  pitch("relHeight", "Rel. Height", "Feet", "RelHeightFeet"),
  pitch("relSide", "Rel. Side", "Feet", "RelSideFeet"),
  pitch("extension", "Extension", "Feet", "ExtensionFeet"),
  pitch("zoneVelo", "Zone Velo.", "MPH", "ZoneSpeedMPH"),
  pitch("plateHeight", "Plate Ht.", "Feet", "PlateLocHeightFeet"),
  pitch("plateSide", "Plate Side", "Feet", "PlateLocSideFeet"),
  pitch("zoneTime", "Zone Time", "Seconds", "ZoneTimeSeconds"),
  pitch("fsri", "FSRI", "Percent", "yt_FSRIPercent"),
  hit("exitVelo", "Exit Velo.", "MPH", "ExitSpeedMPH"),
  hit("launchAngle", "Launch Ang.", "Degrees", "AngleDegrees"),
  hit("distance", "Distance", "Feet", "DistanceFeet"),
  hit("direction", "Direction", "Degrees", "DirectionDegrees"),
  hit("hangTime", "Hang Time", "Seconds", "HangTimeSeconds"),
  hit("hitSpinRate", "Bat'd Spin", "RPM", "HitSpinRateRPM"),
  hit("bearing", "Bearing", "Degrees", "BearingDegrees"),
];

const METRICS_BY_ID = new Map(METRICS.map((metric) => [metric.id, metric]));

export const getMetric = (id: string) => METRICS_BY_ID.get(id);

export const getMetricsForGroup = (group: MetricGroup) =>
  METRICS.filter((metric) => metric.group === group);

// Same defaults as V1 pitchingTiles / hittingTiles (store/slices/tiles).
// V1 hitting repeats Exit Velo, Launch Angle, Distance; duplicates dropped here.
export const DEFAULT_METRIC_TILES: Record<MetricGroup, string[]> = {
  pitch: [
    "velocity",
    "spinRate",
    "efficiency",
    "tilt",
    "vertBreak",
    "inducedVertBreak",
    "horzBreak",
    "relHeight",
    "relSide",
  ],
  hit: [
    "exitVelo",
    "launchAngle",
    "distance",
    "direction",
    "hangTime",
    "hitSpinRate",
  ],
};

/** Display value for a metric, or null when the data is missing. */
export const getMetricValue = (
  metric: MetricDefinition,
  pitchData: PitchData | null | undefined,
  hitData: HitData | null | undefined,
): string | null => {
  const value =
    metric.group === "pitch"
      ? pitchData?.[metric.field]
      : hitData?.[metric.field];

  if (typeof value === "string") return value || null;
  if (typeof value !== "number" || Number.isNaN(value)) return null;

  // Same precision as V1 tiles: one decimal, trailing ".0" dropped
  return String(parseFloat(value.toFixed(1)));
};
